import React from 'react';
import { Link as LinkIcon, Shield, AlertTriangle, CheckCircle, Globe, Search } from 'lucide-react';
import { Button } from '../ui/button';

const URLScannerTab = ({ urlInput, setUrlInput, scanUrl, scanResult, loading }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <LinkIcon className="h-5 w-5 text-blue-500" />
                URL Scanner
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Enter a URL to check if it&apos;s safe or potentially malicious
              </p>
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={urlToScan}
                    onChange={(e) => setUrlToScan(e.target.value)}
                    placeholder="https://example.com/suspicious-link"
                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                  <Button onClick={scanURL} className="bg-orange-600 hover:bg-orange-700">
                    <Search className="h-4 w-4 mr-2" />
                    Scan
                  </Button>
                </div>
                
                <div className="text-xs text-gray-500">
                  <p className="font-medium mb-1">Test URLs:</p>
                  <div className="flex flex-wrap gap-2">
                    <button 
                      onClick={() => setUrlToScan('https://secure-bank-login.com/verify')}
                      className="text-blue-600 hover:underline"
                    >
                      Phishing example
                    </button>
                    <button 
                      onClick={() => setUrlToScan('https://bit.ly/xyz123')}
                      className="text-blue-600 hover:underline"
                    >
                      Shortened URL
                    </button>
                    <button 
                      onClick={() => setUrlToScan('https://google.com')}
                      className="text-blue-600 hover:underline"
                    >
                      Safe URL
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Scan Result */}
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Scan Result</h3>
              
              {scanResult ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border-2 ${
                    scanResult.verdict === 'safe' ? 'bg-green-50 border-green-200' :
                    scanResult.verdict === 'malicious' ? 'bg-red-50 border-red-200' :
                    'bg-orange-50 border-orange-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-lg font-bold ${
                        scanResult.verdict === 'safe' ? 'text-green-700' :
                        scanResult.verdict === 'malicious' ? 'text-red-700' :
                        'text-orange-700'
                      }`}>
                        {scanResult.verdict.toUpperCase()}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        scanResult.risk_score < 20 ? 'bg-green-200 text-green-800' :
                        scanResult.risk_score < 50 ? 'bg-yellow-200 text-yellow-800' :
                        scanResult.risk_score < 70 ? 'bg-orange-200 text-orange-800' :
                        'bg-red-200 text-red-800'
                      }`}>
                        Risk: {scanResult.risk_score}/100
                      </span>
                    </div>
                    <p className="text-sm">{scanResult.recommendation}</p>
                  </div>
                  
                  {scanResult.threats?.length > 0 && (
                    <div>
                      <p className="font-medium text-sm mb-2">Threats Detected:</p>
                      <div className="space-y-2">
                        {scanResult.threats.map((threat) => (
                          <div key={threat.name || threat.type} className="p-2 bg-red-50 rounded border-l-4 border-red-400">
                            <p className="text-sm font-medium text-red-800">{threat.type.replace('_', ' ')}</p>
                            <p className="text-xs text-red-600">{threat.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    Action: <span className="font-medium">{scanResult.action}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <LinkIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Enter a URL and click Scan to check its safety</p>
                </div>
              )}
            </div>
          </div>
);

export default URLScannerTab;
